import React, { useEffect, useState } from "react";
import { DownloadSimple, X } from "@phosphor-icons/react";

const DISMISSED_KEY = "csos.installPromptDismissedAt";
const DISMISS_DAYS = 14;

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    // Already installed (running standalone)
    if (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone) {
      setInstalled(true);
      return;
    }
    // Respect prior dismissal for DISMISS_DAYS
    const dismissedAt = parseInt(localStorage.getItem(DISMISSED_KEY) || "0", 10);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_DAYS * 86400000) return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    const onInstalled = () => {
      setInstalled(true);
      setVisible(false);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const accept = async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      await deferred.userChoice;
    } catch (e) { /* ignore double-prompt errors */ }
    setDeferred(null);
    setVisible(false);
  };

  const dismiss = () => {
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    setVisible(false);
  };

  if (installed || !visible) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm z-50"
      data-testid="install-prompt"
    >
      <div className="surface flex items-start gap-3 p-4 shadow-2xl border-[#dfff00]/20">
        <div className="w-9 h-9 rounded-md bg-[#dfff00] flex items-center justify-center shrink-0">
          <DownloadSimple size={18} weight="bold" color="#09090b" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-heading uppercase tracking-tight text-base">Install courtsideOS</div>
          <p className="text-xs text-zinc-400 mt-0.5">Add to your home screen for one-tap access — works offline too.</p>
          <div className="flex items-center gap-2 mt-3">
            <button
              onClick={accept}
              className="btn-primary text-xs"
              style={{ height: "2rem", padding: "0 0.85rem" }}
              data-testid="install-prompt-accept"
            >
              Install
            </button>
            <button
              onClick={dismiss}
              className="btn-ghost text-xs"
              style={{ height: "2rem", padding: "0 0.85rem" }}
              data-testid="install-prompt-dismiss"
            >
              Not now
            </button>
          </div>
        </div>
        <button
          onClick={dismiss}
          className="btn-ghost shrink-0 -mt-1 -mr-1"
          aria-label="Dismiss"
          data-testid="install-prompt-close"
        >
          <X size={14} weight="bold" />
        </button>
      </div>
    </div>
  );
}
